/**
 * Hexlands admin SPA — single-file router + views (login, reports list,
 * report detail with ban action, bans list, audit log).
 *
 * Kept deliberately small; we'll split when it stops fitting.
 */

import { beginLogin, completeLogin, isLoggedIn, logout } from './auth';
import {
  listReports,
  getReport,
  ReportSummary,
  BanKind,
  Ban,
  listBans,
  createBan,
  deleteBan,
  listAudit,
  AuditEntry,
} from './api';
import { getConfig } from './config';

type Route =
  | { name: 'login' }
  | { name: 'callback'; code: string; state: string }
  | { name: 'list'; kind: 'bug-report' | 'player-report' }
  | { name: 'detail'; reportId: string }
  | { name: 'bans' }
  | { name: 'audit'; yyyymm?: string };

function parseRoute(): Route {
  const { pathname, search, hash } = window.location;
  if (pathname === '/callback') {
    const q = new URLSearchParams(search);
    const code = q.get('code');
    const state = q.get('state');
    if (code && state) return { name: 'callback', code, state };
  }
  const h = (hash || '#/reports/bug-report').replace(/^#/, '');
  const parts = h.split('/').filter(Boolean);
  if (parts[0] === 'reports' && (parts[1] === 'bug-report' || parts[1] === 'player-report')) {
    if (parts[2]) return { name: 'detail', reportId: parts[2] };
    return { name: 'list', kind: parts[1] as 'bug-report' | 'player-report' };
  }
  if (parts[0] === 'bans') return { name: 'bans' };
  if (parts[0] === 'audit') return { name: 'audit', yyyymm: parts[1] };
  return isLoggedIn() ? { name: 'list', kind: 'bug-report' } : { name: 'login' };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) node.append(c);
  return node;
}

function root(): HTMLElement {
  let r = document.getElementById('app');
  if (!r) {
    r = document.createElement('main');
    r.id = 'app';
    document.body.append(r);
  }
  return r;
}

function render(children: Node | Node[]): void {
  const r = root();
  r.innerHTML = '';
  if (Array.isArray(children)) for (const c of children) r.append(c);
  else r.append(children);
}

function header(): HTMLElement {
  const nav = el(
    'nav',
    { class: 'top' },
    el('span', { class: 'brand' }, 'Hexlands Admin'),
    el('a', { href: '#/reports/bug-report' }, 'Bug reports'),
    el('a', { href: '#/reports/player-report' }, 'Player reports'),
    el('a', { href: '#/bans' }, 'Bans'),
    el('a', { href: '#/audit' }, 'Audit'),
  );
  if (isLoggedIn()) {
    const out = el('button', { class: 'link' }, 'Logout');
    out.addEventListener('click', () => logout());
    nav.append(out);
  }
  return nav;
}

function viewLogin(): void {
  const btn = el('button', { class: 'primary' }, 'Sign in with Identity Center');
  btn.addEventListener('click', () => beginLogin());
  render([
    header(),
    el('section', { class: 'center' }, el('h1', {}, 'Hexlands Admin'), btn),
  ]);
}

async function viewCallback(code: string, state: string): Promise<void> {
  render(el('p', {}, 'Completing sign-in…'));
  try {
    await completeLogin(code, state);
    window.history.replaceState({}, '', '/#/reports/bug-report');
    route();
  } catch (e) {
    render(el('pre', { class: 'error' }, String(e)));
  }
}

async function viewList(kind: 'bug-report' | 'player-report'): Promise<void> {
  if (!isLoggedIn()) return viewLogin();
  render([header(), el('p', {}, `Loading ${kind}s…`)]);
  try {
    const { items, nextCursor } = await listReports(kind);
    const rows = items.length === 0
      ? [el('tr', {}, el('td', { colspan: '4' }, 'No reports'))]
      : items.map((it) => renderRow(it, kind));
    const table = el(
      'table',
      { class: 'reports' },
      el('thead', {}, el('tr', {}, ...headerCells(kind).map((h) => el('th', {}, h)))),
      el('tbody', {}, ...rows),
    );
    const content: Node[] = [header(), el('h2', {}, kindLabel(kind)), table];
    if (nextCursor) {
      const more = el('button', {}, 'Load more');
      more.addEventListener('click', async () => {
        const next = await listReports(kind, nextCursor);
        const tbody = table.querySelector('tbody')!;
        for (const it of next.items) tbody.append(renderRow(it, kind));
        if (!next.nextCursor) more.remove();
      });
      content.push(more);
    }
    render(content);
  } catch (e) {
    render([header(), el('pre', { class: 'error' }, String(e))]);
  }
}

function headerCells(kind: 'bug-report' | 'player-report'): string[] {
  return kind === 'bug-report'
    ? ['When', 'Game', 'Summary', '']
    : ['When', 'Reporter → Reported', 'Reason', ''];
}

function renderRow(it: ReportSummary, kind: 'bug-report' | 'player-report'): HTMLTableRowElement {
  const when = new Date(it.receivedAt).toISOString().replace('T', ' ').replace(/\..*/, '');
  const link = el('a', { href: `#/reports/${kind}/${it.reportId}` }, 'Open');
  if (kind === 'bug-report') {
    return el(
      'tr',
      {},
      el('td', {}, when),
      el('td', { class: 'mono' }, it.gameId ?? '—'),
      el('td', {}, truncate(it.description ?? '', 120)),
      el('td', {}, link),
    );
  }
  return el(
    'tr',
    {},
    el('td', {}, when),
    el('td', { class: 'mono' }, `${it.reporterPlayerId ?? '?'} → ${it.reportedPlayerId ?? '?'}`),
    el('td', {}, truncate(it.reason ?? '', 120)),
    el('td', {}, link),
  );
}

function kindLabel(kind: 'bug-report' | 'player-report'): string {
  return kind === 'bug-report' ? 'Bug reports' : 'Player reports';
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

async function viewDetail(reportId: string): Promise<void> {
  if (!isLoggedIn()) return viewLogin();
  render([header(), el('p', {}, `Loading ${reportId}…`)]);
  try {
    const item = (await getReport(reportId)) as Record<string, unknown>;
    const kind = item.kind as 'bug-report' | 'player-report' | undefined;

    const children: Node[] = [header(), el('h2', {}, `${kindLabel(kind ?? 'bug-report')} — ${reportId}`)];

    // Summary panel
    const summary = el('div', { class: 'detail-summary' },
      field('When', new Date(Number(item.receivedAt)).toISOString()),
      field('Kind', String(kind ?? '—')),
      field('Game', String(item.gameId ?? '—')),
    );
    children.push(summary);

    // Kind-specific actions + links
    if (kind === 'player-report') {
      const reportedClientId = item.reportedClientId as string | null | undefined;
      const reportedPlayerId = item.reportedPlayerId as string | undefined;
      const reporterPlayerId = item.reporterPlayerId as string | undefined;
      children.push(
        el('div', { class: 'detail-summary' },
          field('Reporter', `${reporterPlayerId ?? '—'} (client: ${item.reporterClientId ?? '—'})`),
          field('Reported', `${reportedPlayerId ?? '—'} (client: ${reportedClientId ?? 'unknown'})`),
          field('Reason', String(item.reason ?? '—')),
        ),
      );
      const messages = Array.isArray(item.recentMessages) ? item.recentMessages : [];
      if (messages.length > 0) {
        children.push(recentMessagesPanel(messages, reporterPlayerId, reportedPlayerId));
      }
      children.push(
        banForm({
          reportedClientId: reportedClientId ?? null,
          sourceReportId: reportId,
          defaultReason: String(item.reason ?? 'Reported by player').slice(0, 200),
        }),
      );
    } else if (kind === 'bug-report') {
      const desc = item.description;
      if (typeof desc === 'string' && desc.trim()) {
        children.push(
          el('div', { class: 'detail-summary' },
            el('div', { class: 'field' },
              el('span', { class: 'label' }, 'Description'),
              el('pre', { class: 'json' }, desc),
            ),
          ),
        );
      }
      const link = cloudWatchDeepLink(item);
      if (link) {
        children.push(
          el('p', {},
            el('a', { href: link, target: '_blank', rel: 'noreferrer noopener' },
              'Open server logs for this bug in CloudWatch ↗'),
          ),
        );
      }
    }

    children.push(
      el('details', { open: '' },
        el('summary', {}, 'Raw JSON'),
        el('pre', { class: 'json' }, JSON.stringify(item, null, 2)),
      ),
    );

    render(children);
  } catch (e) {
    render([header(), el('pre', { class: 'error' }, String(e))]);
  }
}

function recentMessagesPanel(
  messages: unknown[],
  reporterPlayerId: string | undefined,
  reportedPlayerId: string | undefined,
): HTMLElement {
  const wrap = el('div', { class: 'detail-summary' },
    el('h3', {}, 'Recent chat'),
    el('p', { class: 'muted' },
      `Up to 10 most-recent chat messages captured at report time. ` +
      `Reporter = blue, reported = red, other players = muted.`),
  );
  const list = el('div', { class: 'chat-log' });
  for (const raw of messages) {
    const m = raw as { playerId?: string; text?: string; timestamp?: number };
    const who = m.playerId ?? '?';
    let role = 'other';
    if (who === reportedPlayerId) role = 'reported';
    else if (who === reporterPlayerId) role = 'reporter';
    const when = m.timestamp ? new Date(m.timestamp).toISOString().replace('T', ' ').replace(/\..*/, '') : '';
    list.append(el('div', { class: `chat-bubble chat-${role}` },
      el('div', { class: 'chat-meta' }, `${who}${when ? ' · ' + when : ''}`),
      el('div', { class: 'chat-text' }, String(m.text ?? '')),
    ));
  }
  wrap.append(list);
  return wrap;
}

function field(label: string, value: string): HTMLElement {
  return el('div', { class: 'field' },
    el('span', { class: 'label' }, label),
    el('span', { class: 'value' }, value),
  );
}

interface BanFormProps {
  reportedClientId: string | null;
  sourceReportId: string;
  defaultReason: string;
}

function banForm(p: BanFormProps): HTMLElement {
  const wrap = el('div', { class: 'ban-form' });
  if (!p.reportedClientId) {
    wrap.append(
      el('p', { class: 'muted' },
        'Ban unavailable — reportedClientId was not captured at report time. ' +
        'You can manually ban by clientId or token via the Bans page.'),
    );
    return wrap;
  }

  const reason = el('textarea', { rows: '3' }) as HTMLTextAreaElement;
  reason.value = p.defaultReason;

  const button = el('button', { class: 'danger' }, 'Ban this player') as HTMLButtonElement;
  const status = el('span', { class: 'status' });

  button.addEventListener('click', async () => {
    button.disabled = true;
    status.textContent = 'Banning…';
    try {
      const res = await createBan({
        kind: 'clientId',
        value: p.reportedClientId!,
        reason: reason.value.trim() || 'Reported by player',
        sourceReportId: p.sourceReportId,
      });
      const companionsText = res.companions.length
        ? ` (+${res.companions.length} companion token ${res.companions.length === 1 ? 'ban' : 'bans'})`
        : '';
      status.textContent = `Banned${companionsText}`;
      button.textContent = 'Banned';
    } catch (e) {
      status.textContent = String(e);
      button.disabled = false;
    }
  });

  wrap.append(
    el('h3', {}, 'Ban player'),
    el('label', {},
      'Reason',
      reason,
    ),
    el('div', { class: 'row' }, button, status),
  );
  return wrap;
}

function cloudWatchDeepLink(item: Record<string, unknown>): string | null {
  const cfg = getConfig();
  const reportId = item.reportId as string | undefined;
  const receivedAt = item.receivedAt as number | undefined;
  if (!reportId || !receivedAt) return null;

  // 30-minute window centred on the report time, URL-encoded for
  // CloudWatch Logs Insights. Log group is the default-router Lambda since
  // that's where the bug-report ingest + surrounding game activity logs.
  const start = new Date(receivedAt - 15 * 60_000).toISOString();
  const end = new Date(receivedAt + 15 * 60_000).toISOString();
  const logGroup = `/aws/lambda/hexlands-default-${cfg.stage}`;
  const query = `fields @timestamp, @message | filter @message like /${reportId}/ | sort @timestamp desc | limit 200`;

  // CloudWatch Logs Insights deep link — format ripped from the console.
  // Double-encoded segments are intentional (AWS requires `*7E` for ~, etc).
  const payload = encodeURIComponent(
    `~(end~'${end}~start~'${start}~timeType~'ABSOLUTE~tz~'UTC~editorString~'${query}~source~(~'${logGroup}'))`,
  );
  return `https://${cfg.region}.console.aws.amazon.com/cloudwatch/home?region=${cfg.region}#logsV2:logs-insights$3FqueryDetail$3D${payload}`;
}

// ── Bans list view ─────────────────────────────────────────────────────────

async function viewBans(): Promise<void> {
  if (!isLoggedIn()) return viewLogin();
  render([header(), el('p', {}, 'Loading bans…')]);
  try {
    const { items } = await listBans();
    const tbody = el('tbody', {});
    if (items.length === 0) {
      tbody.append(el('tr', {}, el('td', { colspan: '5' }, 'No active bans')));
    } else {
      for (const b of items) tbody.append(banRow(b));
    }
    const table = el(
      'table',
      { class: 'reports' },
      el('thead', {}, el('tr', {},
        el('th', {}, 'When'),
        el('th', {}, 'Kind'),
        el('th', {}, 'Value'),
        el('th', {}, 'Reason / By'),
        el('th', {}, ''),
      )),
      tbody,
    );

    render([header(), el('h2', {}, 'Active bans'), table, manualBanForm()]);
  } catch (e) {
    render([header(), el('pre', { class: 'error' }, String(e))]);
  }
}

function banRow(b: Ban): HTMLTableRowElement {
  const when = new Date(b.createdAt).toISOString().replace('T', ' ').replace(/\..*/, '');
  const unbanBtn = el('button', {}, 'Unban') as HTMLButtonElement;
  const row = el('tr', {},
    el('td', {}, when),
    el('td', { class: 'mono' }, b.kind),
    el('td', { class: 'mono' }, b.value),
    el('td', {},
      b.reason,
      el('br'),
      el('span', { class: 'muted' }, `by ${b.createdBy}`),
    ),
    el('td', {}, unbanBtn),
  );
  unbanBtn.addEventListener('click', async () => {
    if (!confirm(`Unban ${b.kind} ${b.value}?`)) return;
    unbanBtn.disabled = true;
    unbanBtn.textContent = 'Unbanning…';
    try {
      await deleteBan(b.kind, b.value);
      row.remove();
    } catch (e) {
      unbanBtn.textContent = 'Failed';
      alert(String(e));
    }
  });
  return row;
}

function manualBanForm(): HTMLElement {
  const kindSel = el('select', {}) as HTMLSelectElement;
  kindSel.append(new Option('clientId', 'clientId'), new Option('token', 'token'));
  const valueInput = el('input', { type: 'text', placeholder: 'UUID' }) as HTMLInputElement;
  const reasonInput = el('input', { type: 'text', placeholder: 'Reason' }) as HTMLInputElement;
  const submit = el('button', { class: 'primary' }, 'Add ban') as HTMLButtonElement;
  const status = el('span', { class: 'status' });

  submit.addEventListener('click', async () => {
    const kind = kindSel.value as BanKind;
    const value = valueInput.value.trim();
    const reason = reasonInput.value.trim();
    if (!value || !reason) {
      status.textContent = 'Value + reason required';
      return;
    }
    submit.disabled = true;
    status.textContent = 'Banning…';
    try {
      await createBan({ kind, value, reason });
      status.textContent = 'Banned — reloading…';
      setTimeout(() => viewBans(), 400);
    } catch (e) {
      status.textContent = String(e);
      submit.disabled = false;
    }
  });

  return el('div', { class: 'ban-form' },
    el('h3', {}, 'Add manual ban'),
    el('div', { class: 'row' }, kindSel, valueInput, reasonInput, submit, status),
  );
}

// ── Audit view ─────────────────────────────────────────────────────────────

async function viewAudit(yyyymm?: string): Promise<void> {
  if (!isLoggedIn()) return viewLogin();
  render([header(), el('p', {}, 'Loading audit log…')]);
  try {
    const res = await listAudit({ yyyymm });
    const tbody = el('tbody', {});
    if (res.items.length === 0) {
      tbody.append(el('tr', {}, el('td', { colspan: '4' }, 'No entries this month')));
    } else {
      for (const a of res.items) tbody.append(auditRow(a));
    }
    const table = el('table', { class: 'reports' },
      el('thead', {}, el('tr', {},
        el('th', {}, 'When'),
        el('th', {}, 'Actor'),
        el('th', {}, 'Action'),
        el('th', {}, 'Target / Details'),
      )),
      tbody,
    );

    const prev = shiftMonth(res.yyyymm, -1);
    const next = shiftMonth(res.yyyymm, +1);
    const nav = el('div', { class: 'row' },
      el('a', { href: `#/audit/${prev}` }, `← ${prev}`),
      el('span', { class: 'muted' }, `  ${res.yyyymm}  `),
      el('a', { href: `#/audit/${next}` }, `${next} →`),
    );

    const content: Node[] = [
      header(),
      el('h2', {}, `Admin audit — ${res.yyyymm}`),
      nav,
      table,
    ];

    let cursor = res.nextCursor;
    if (cursor) {
      const more = el('button', {}, 'Load more') as HTMLButtonElement;
      more.addEventListener('click', async () => {
        more.disabled = true;
        more.textContent = 'Loading…';
        try {
          const page = await listAudit({ yyyymm: res.yyyymm, cursor: cursor! });
          for (const a of page.items) tbody.append(auditRow(a));
          cursor = page.nextCursor;
          if (!cursor) more.remove();
          else {
            more.disabled = false;
            more.textContent = 'Load more';
          }
        } catch (e) {
          more.textContent = 'Failed';
          alert(String(e));
        }
      });
      content.push(more);
    }
    render(content);
  } catch (e) {
    render([header(), el('pre', { class: 'error' }, String(e))]);
  }
}

function shiftMonth(yyyymm: string, delta: number): string {
  const y = parseInt(yyyymm.slice(0, 4), 10);
  const m = parseInt(yyyymm.slice(4, 6), 10);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function auditRow(a: AuditEntry): HTMLTableRowElement {
  const when = new Date(a.timestamp).toISOString().replace('T', ' ').replace(/\..*/, '');
  const actor = a.actorLabel ? `${a.actorLabel}` : a.actor;
  return el('tr', {},
    el('td', {}, when),
    el('td', { class: 'mono' }, actor),
    el('td', {}, a.action),
    el('td', { class: 'mono' },
      `${a.target.kind}: ${a.target.value}`,
      a.details ? el('div', { class: 'muted' }, JSON.stringify(a.details)) : document.createTextNode(''),
    ),
  );
}

// ── router ─────────────────────────────────────────────────────────────────

function route(): void {
  const r = parseRoute();
  switch (r.name) {
    case 'login': return viewLogin();
    case 'callback': void viewCallback(r.code, r.state); return;
    case 'list': void viewList(r.kind); return;
    case 'detail': void viewDetail(r.reportId); return;
    case 'bans': void viewBans(); return;
    case 'audit': void viewAudit(r.yyyymm); return;
  }
}

window.addEventListener('hashchange', route);
window.addEventListener('DOMContentLoaded', route);
